import i18n from "@/i18n";

import {
  ChoiceOption,
  ChoicesFromRef,
  QuestionRoutingRule,
  QuestionVisibilityRule,
  QuestionVisibilityRuleExpr,
  conditionOperatorPolarity,
  isVisibilityRuleGroup,
} from "@digital-twins/shared-types";

import { VisibilityRuleCandidate } from "../hooks/useQuestionnaireWorkspace";
import { RoutingOptionOutcome } from "../components/questionSettingsTypes";

/**
 * Read-only context for the one-line logic summaries and the per-option
 * routing outcome chips. Nothing here evaluates SurveyJS expressions: every
 * string is read from the authored rule.
 */
export interface LogicSummaryContext {
  hostStableKey: string;
  /** Rule dependency candidates; used for option, row and title labels. */
  candidates: Array<VisibilityRuleCandidate>;
  /** Display label per stableKey, e.g. "Q2". */
  questionLabelByStableKey: Map<string, string>;
}

const MAX_QUOTED_LABEL_LENGTH = 40;
const ELLIPSIS = "…";

function truncateLabel(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length <= MAX_QUOTED_LABEL_LENGTH) {
    return trimmed;
  }
  return `${trimmed.slice(0, MAX_QUOTED_LABEL_LENGTH - 1).trimEnd()}${ELLIPSIS}`;
}

function findCandidate(
  stableKey: string,
  ctx: LogicSummaryContext,
): VisibilityRuleCandidate | undefined {
  return ctx.candidates.find((candidate) => candidate.stableKey === stableKey);
}

function questionLabel(stableKey: string, ctx: LogicSummaryContext): string {
  const label = ctx.questionLabelByStableKey.get(stableKey);
  if (label) {
    return label;
  }
  const candidate = findCandidate(stableKey, ctx);
  if (candidate && candidate.title.trim().length > 0) {
    return truncateLabel(candidate.title);
  }
  return stableKey;
}

function leafSubject(
  leaf: QuestionVisibilityRule,
  ctx: LogicSummaryContext,
): string {
  const base =
    leaf.dependsOnStableKey === ctx.hostStableKey
      ? i18n.t("questionnaire.logic.subjectThisAnswer", { ns: "app" })
      : questionLabel(leaf.dependsOnStableKey, ctx);
  const candidate = findCandidate(leaf.dependsOnStableKey, ctx);
  if (candidate?.kind !== "matrix_rating" || !leaf.matrixRowValue) {
    return base;
  }
  const row = (candidate.matrixRows ?? []).find(
    (matrixRow) => matrixRow.value === leaf.matrixRowValue,
  );
  const rowLabel = truncateLabel(row?.label ?? leaf.matrixRowValue);
  return `${base} (${rowLabel})`;
}

function optionLabel(
  value: string,
  candidate: VisibilityRuleCandidate | undefined,
): string {
  const option = candidate?.options.find((item) => item.value === value);
  return truncateLabel(option?.label?.trim() || value);
}

function formatNumber(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return ELLIPSIS;
  }
  return String(value);
}

function summarizeChoiceLeaf(
  leaf: QuestionVisibilityRule,
  subject: string,
  candidate: VisibilityRuleCandidate | undefined,
): string {
  const values = Array.isArray(leaf.values) ? leaf.values : [];
  const isNegated = conditionOperatorPolarity(leaf.operator) === "exclude";
  if (values.length === 1) {
    const value = optionLabel(values[0], candidate);
    if (isNegated) {
      return i18n.t("questionnaire.logic.cond.isNotValue", {
        ns: "app",
        subject,
        value,
      });
    }
    return i18n.t("questionnaire.logic.cond.isValue", {
      ns: "app",
      subject,
      value,
    });
  }
  const count = values.length;
  if (isNegated) {
    return i18n.t("questionnaire.logic.cond.isNoneOf", {
      ns: "app",
      subject,
      count,
    });
  }
  return i18n.t("questionnaire.logic.cond.isAnyOf", {
    ns: "app",
    subject,
    count,
  });
}

function summarizeRankingLeaf(
  leaf: QuestionVisibilityRule,
  subject: string,
  candidate: VisibilityRuleCandidate | undefined,
): string {
  const values = Array.isArray(leaf.values) ? leaf.values : [];
  const joinedLabels = values
    .map((value) => {
      const option = candidate?.options.find((item) => item.value === value);
      return option?.label?.trim() || value;
    })
    .join(", ");
  const value = truncateLabel(joinedLabels);
  const rank = formatNumber(leaf.rankK);
  if (leaf.operator === "rank_in_top") {
    return i18n.t("questionnaire.logic.cond.rankInTop", {
      ns: "app",
      subject,
      value,
      rank,
    });
  }
  if (leaf.operator === "rank_in_bottom") {
    return i18n.t("questionnaire.logic.cond.rankInBottom", {
      ns: "app",
      subject,
      value,
      rank,
    });
  }
  return i18n.t("questionnaire.logic.cond.rankEquals", {
    ns: "app",
    subject,
    value,
    rank,
  });
}

function summarizeLeaf(
  leaf: QuestionVisibilityRule,
  ctx: LogicSummaryContext,
): string {
  const subject = leafSubject(leaf, ctx);
  const candidate = findCandidate(leaf.dependsOnStableKey, ctx);
  switch (leaf.operator) {
    case "equals":
    case "not_equals":
    case "any_of":
    case "none_of":
      return summarizeChoiceLeaf(leaf, subject, candidate);
    case "eq":
      return i18n.t("questionnaire.logic.cond.eq", {
        ns: "app",
        subject,
        value: formatNumber(leaf.threshold),
      });
    case "gt":
      return i18n.t("questionnaire.logic.cond.gt", {
        ns: "app",
        subject,
        value: formatNumber(leaf.threshold),
      });
    case "gte":
      return i18n.t("questionnaire.logic.cond.gte", {
        ns: "app",
        subject,
        value: formatNumber(leaf.threshold),
      });
    case "lt":
      return i18n.t("questionnaire.logic.cond.lt", {
        ns: "app",
        subject,
        value: formatNumber(leaf.threshold),
      });
    case "lte":
      return i18n.t("questionnaire.logic.cond.lte", {
        ns: "app",
        subject,
        value: formatNumber(leaf.threshold),
      });
    case "between":
      return i18n.t("questionnaire.logic.cond.between", {
        ns: "app",
        subject,
        min: formatNumber(leaf.threshold),
        max: formatNumber(leaf.thresholdHigh),
      });
    case "is_not_applicable":
      return i18n.t("questionnaire.logic.cond.isNotApplicable", {
        ns: "app",
        subject,
      });
    case "rank_equals":
    case "rank_in_top":
    case "rank_in_bottom":
      return summarizeRankingLeaf(leaf, subject, candidate);
    default:
      return subject;
  }
}

/** A group holding one rule reads (and routes) exactly like that rule. */
function singleLeafOf(
  expr: QuestionVisibilityRuleExpr,
): QuestionVisibilityRule | null {
  if (!isVisibilityRuleGroup(expr)) {
    return expr;
  }
  if (expr.rules.length === 1) {
    return expr.rules[0];
  }
  return null;
}

function summarizeCondition(
  expr: QuestionVisibilityRuleExpr,
  ctx: LogicSummaryContext,
): string {
  const leaf = singleLeafOf(expr);
  if (leaf) {
    return summarizeLeaf(leaf, ctx);
  }
  const rules = isVisibilityRuleGroup(expr) ? expr.rules : [expr];
  const count = rules.length;
  if (isVisibilityRuleGroup(expr) && expr.join === "or") {
    return i18n.t("questionnaire.logic.cond.any", { ns: "app", count });
  }
  return i18n.t("questionnaire.logic.cond.all", { ns: "app", count });
}

/** One-line summary of a visibility rule ("Asked only if Q2 is any of 3 options"). */
export function summarizeVisibilityRule(
  rule: QuestionVisibilityRuleExpr,
  ctx: LogicSummaryContext,
): string {
  return i18n.t("questionnaire.logic.summary.visibility", {
    ns: "app",
    condition: summarizeCondition(rule, ctx),
  });
}

/** One-line summary of a routing rule (disqualify, end survey or skip). */
export function summarizeRoutingRule(
  rule: QuestionRoutingRule,
  ctx: LogicSummaryContext,
): string {
  const condition = summarizeCondition(rule.when, ctx);
  if (rule.action === "skip_to") {
    const target = rule.targetStableKey
      ? questionLabel(rule.targetStableKey, ctx)
      : ELLIPSIS;
    return i18n.t("questionnaire.logic.summary.skipTo", {
      ns: "app",
      target,
      condition,
    });
  }
  if (rule.disposition === "screened_out") {
    return i18n.t("questionnaire.logic.summary.disqualify", {
      ns: "app",
      condition,
    });
  }
  return i18n.t("questionnaire.logic.summary.endSurvey", {
    ns: "app",
    condition,
  });
}

/** One-line summary of a carry-forward reference ("Options: those selected in Q3"). */
export function summarizeChoicesFrom(
  choicesFrom: ChoicesFromRef,
  ctx: LogicSummaryContext,
): string {
  const source = questionLabel(choicesFrom.sourceStableKey, ctx);
  if (choicesFrom.mode === "unselected") {
    return i18n.t("questionnaire.logic.summary.optionsUnselected", {
      ns: "app",
      source,
    });
  }
  if (choicesFrom.mode === "all") {
    return i18n.t("questionnaire.logic.summary.optionsAll", {
      ns: "app",
      source,
    });
  }
  return i18n.t("questionnaire.logic.summary.optionsSelected", {
    ns: "app",
    source,
  });
}

function outcomeForRule(
  rule: QuestionRoutingRule,
  ctx: LogicSummaryContext,
): RoutingOptionOutcome | null {
  if (rule.action === "skip_to") {
    if (!rule.targetStableKey) {
      return null;
    }
    return {
      kind: "skips_to",
      targetLabel: questionLabel(rule.targetStableKey, ctx),
    };
  }
  if (rule.disposition === "screened_out") {
    return { kind: "disqualified" };
  }
  return { kind: "ends_survey" };
}

/**
 * Per-option outcome of the host's own routing rule, keyed by option value.
 * Conservative by design: only a single choice condition on the host itself
 * yields chips. Negated operators mark the complement only on a single-select
 * host, because on multi-select the compiled `notcontains` chain means a
 * complement pick alongside a listed pick does not trigger the route.
 * Compound rules and rules on earlier questions return an empty map (the
 * summary line covers them).
 */
export function routingOutcomeByOptionValue(
  rule: QuestionRoutingRule | undefined,
  host: {
    stableKey: string;
    isMultiSelect: boolean;
    options: Array<ChoiceOption>;
  },
  ctx: LogicSummaryContext,
): Map<string, RoutingOptionOutcome> {
  const outcomes = new Map<string, RoutingOptionOutcome>();
  if (!rule) {
    return outcomes;
  }
  const leaf = singleLeafOf(rule.when);
  if (!leaf || leaf.dependsOnStableKey !== host.stableKey) {
    return outcomes;
  }
  const outcome = outcomeForRule(rule, ctx);
  if (!outcome) {
    return outcomes;
  }
  const listed = new Set(Array.isArray(leaf.values) ? leaf.values : []);
  const polarity = conditionOperatorPolarity(leaf.operator);
  if (polarity === "include") {
    for (const option of host.options) {
      if (listed.has(option.value)) {
        outcomes.set(option.value, outcome);
      }
    }
    return outcomes;
  }
  if (polarity === "exclude") {
    if (host.isMultiSelect) {
      return outcomes;
    }
    for (const option of host.options) {
      if (!listed.has(option.value)) {
        outcomes.set(option.value, outcome);
      }
    }
  }
  return outcomes;
}
